import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";
import FilterBar from "../../components/admin/FilterBar";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;
const emptyForm = { title: "", duration: "", description: "", eligibility: "", career_scope: "", images: [], imagePreviews: [] };

const compressImage = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let [w, h] = [img.width, img.height];
      if (w > 800) { h *= 800 / w; w = 800; }
      if (h > 600) { w *= 600 / h; h = 600; }
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() })), "image/jpeg", 0.9);
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
});

const CoursesCRUD = () => {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editCourse, setEditCourse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0, total_pages: 1 });

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCourses(); }, []);

  const fetchCourses = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URI}/entities?category=course&page=${page}&limit=${pagination.page_size}`, getAuthHeaders());
      if (res.data?.data) {
        setCourses(res.data.data.map((i) => ({ id: i.id, title: i.data.title || "", duration: i.data.duration || "", description: i.data.description || "", eligibility: i.data.eligibility || "", career_scope: i.data.career_scope || "", images: i.data.images || [] })));
        setPagination({ page: res.data.page, page_size: res.data.limit, total: res.data.total, total_pages: Math.ceil(res.data.total / res.data.limit) });
      }
      setError("");
    } catch { setError("Failed to fetch courses"); }
    finally { setLoading(false); }
  };

  const openAdd = () => { setForm(emptyForm); setEditCourse(null); setDialogOpen(true); };
  const openEdit = (c) => { setForm({ title: c.title, duration: c.duration, description: c.description, eligibility: c.eligibility, career_scope: c.career_scope, images: [], imagePreviews: c.images || [] }); setEditCourse(c); setDialogOpen(true); };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files);
    const compressed = await Promise.all(files.filter((f) => f.size <= 5 * 1024 * 1024).map(compressImage));
    const previews = compressed.map((f) => URL.createObjectURL(f));
    setForm((f) => ({ ...f, images: [...f.images, ...compressed], imagePreviews: [...f.imagePreviews, ...previews] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.warning("Please enter a course title"); return; }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("title", form.title); fd.append("duration", form.duration);
      fd.append("description", form.description); fd.append("eligibility", form.eligibility);
      fd.append("career_scope", form.career_scope);
      if (editCourse) fd.append("appendImages", "true");
      form.images.forEach((img) => fd.append("images", img));
      const hdrs = { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` } };
      if (editCourse) {
        await axios.put(`${BASE_URI}/entities/${editCourse.id}?category=course`, fd, hdrs);
        toast.success("Course updated!");
      } else {
        await axios.post(`${BASE_URI}/entities?category=course`, fd, hdrs);
        toast.success("Course created!");
      }
      setDialogOpen(false); fetchCourses(pagination.page);
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const deleteCourse = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(`${BASE_URI}/entities/${deleteId}?category=course`, getAuthHeaders());
      toast.success("Deleted successfully!");
      setDeleteId(null);
      fetchCourses(courses.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page);
    } catch { toast.error("Failed to delete."); }
    finally { setDeleteLoading(false); }
  };

  const filtered = courses.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Courses Management"
        description="Manage courses offered by the organisation"
        action={<Button onClick={openAdd}><Plus className="h-4 w-4" /> Add Course</Button>}
      />
      <InfoBanner
        title="Courses Management"
        description="Add and update courses with detailed descriptions, eligibility, and career scope information."
        capabilities={["Multi-image upload", "Eligibility & career scope fields", "Duration tracking"]}
      />
      <FilterBar search={search} onSearch={setSearch} placeholder="Search by title…" count={filtered.length} />
      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[90px]">Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-[90px]">Duration</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Eligibility</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-400">
                <div className="flex items-center justify-center gap-2"><div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />Loading…</div>
              </TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-400">No courses yet.</TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  {c.images?.[0] ? <img src={c.images[0]} alt={c.title} className="h-14 w-20 rounded-lg object-cover border border-gray-100" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="h-14 w-20 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-xs">No img</div>}
                </TableCell>
                <TableCell className="font-medium text-gray-900"><p className="line-clamp-2 max-w-[160px]">{c.title}</p></TableCell>
                <TableCell className="text-gray-600 text-sm">{c.duration}</TableCell>
                <TableCell className="text-gray-600 text-sm max-w-[180px]"><p className="line-clamp-2">{c.description}</p></TableCell>
                <TableCell className="text-gray-600 text-sm max-w-[160px]"><p className="line-clamp-2">{c.eligibility}</p></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => setDeleteId(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button size="sm" variant="outline" onClick={() => fetchCourses(pagination.page - 1)} disabled={pagination.page === 1 || loading}>Previous</Button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
              <Button key={p} size="sm" variant={p === pagination.page ? "default" : "outline"} onClick={() => fetchCourses(p)} disabled={loading}>{p}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => fetchCourses(pagination.page + 1)} disabled={pagination.page === pagination.total_pages || loading}>Next</Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete course?"
        description="This action cannot be undone."
        onConfirm={deleteCourse}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editCourse ? "Edit Course" : "Add New Course"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>Course Title <span className="text-red-500">*</span></Label>
                <Input placeholder="Course title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Duration</Label>
                <Input placeholder="e.g. 6 months" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea placeholder="Course description…" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Eligibility</Label>
              <Textarea placeholder="Eligibility criteria…" rows={2} value={form.eligibility} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Career Scope</Label>
              <Textarea placeholder="Career opportunities…" rows={2} value={form.career_scope} onChange={(e) => setForm({ ...form, career_scope: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Course Images</Label>
              <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors w-fit">
                <Upload className="h-4 w-4" /> Upload images
                <input type="file" accept="image/*" multiple onChange={handleImages} className="hidden" />
              </label>
              {form.imagePreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.imagePreviews.map((url, i) => (
                    <img key={i} src={url} alt="" className="h-14 w-20 rounded-lg object-cover border border-gray-200" />
                  ))}
                </div>
              )}
            </div>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading}>{loading ? "Saving…" : editCourse ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoursesCRUD;
